import React from "react";
import { cn } from "@/lib/utils";

interface SkillScore {
  communication?: number;
  technical?: number;
  punctuality?: number;
  teamwork?: number;
  [key: string]: number | undefined;
}

interface SkillScoreDisplayProps {
  scores: SkillScore;
  title?: string;
  compact?: boolean;
  className?: string;
}

const SKILL_CONFIG: Record<string, { label: string }> = {
  communication: { label: "Communication" },
  technical: { label: "Technical Proficiency" },
  punctuality: { label: "Punctuality" },
  teamwork: { label: "Teamwork" },
};

const SKILL_KEYS = ["communication", "technical", "punctuality", "teamwork"];

/** Tone classes for a score out of 5: [text, bar fill]. */
function getScoreTone(score: number): { text: string; bar: string } {
  if (score >= 4.5) return { text: "text-success", bar: "bg-success" };
  if (score >= 3) return { text: "text-warning", bar: "bg-warning" };
  return { text: "text-destructive", bar: "bg-destructive" };
}

function ScoreBar({ score, barClass, compact }: { score: number; barClass: string; compact?: boolean }) {
  const percentage = Math.max(0, Math.min(100, (score / 5) * 100));
  return (
    <div
      className={cn("relative flex-1 overflow-hidden rounded-full bg-hairline", compact ? "h-1" : "h-1.5")}
      role="progressbar"
      aria-valuemin={0}
      aria-valuemax={5}
      aria-valuenow={score}
    >
      <div className={cn("h-full rounded-full transition-all", barClass)} style={{ width: `${percentage}%` }} />
    </div>
  );
}

export default function SkillScoreDisplay({
  scores,
  title,
  compact = false,
  className,
}: SkillScoreDisplayProps) {
  return (
    <div className={className}>
      {title && <p className="eyebrow mb-3 text-muted-foreground">{title}</p>}
      <div className={cn("flex flex-col", compact ? "gap-2" : "gap-3")}>
        {SKILL_KEYS.map((key) => {
          const score = scores[key];
          if (score === undefined) return null;
          const config = SKILL_CONFIG[key];
          const tone = getScoreTone(score);

          return (
            <div key={key}>
              {!compact && (
                <p className="mb-1 text-caption text-body">{config.label}</p>
              )}
              <div className="flex items-center gap-2">
                {compact && (
                  <span
                    className="w-3.5 text-[11px] font-medium text-muted-foreground"
                    title={config.label}
                  >
                    {key.charAt(0).toUpperCase()}
                  </span>
                )}
                <ScoreBar score={score} barClass={tone.bar} compact={compact} />
                <span
                  className={cn(
                    "min-w-7 text-right font-mono text-caption font-medium tabular-nums",
                    tone.text,
                  )}
                >
                  {score.toFixed(1)}
                </span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
